(ns very.http.core
  (:require
   [ajax.core :as http]
   [ajax.simple :as simple]
   [ajax.interceptors :as intc]
   [ajax.protocols :as proto]
   [cljs.spec.alpha :as s]
   [very.re-frame.core :as rf]
   [very.http.specs :as sp]
   [camel-snake-kebab.core :as k]
   [goog.json :as json]
   [storage.core :as storage]
   [runtime.core :refer [env]]
   [util.core :refer [transform-keys]]))

(defn- failure? [status-code]
  (or (> status-code 499)
      (= 0 status-code)
      (= -1 status-code)))

(defn- error? [resp]
  (and (contains? resp :errors)
       (not-empty (:errors resp))))

(defn- unauthenticated? [resp]
  (some #(= 401 (:code %)) (:errors resp)))

(defn- dsl->handlers
  "Given a DSL described *elsewhere*, invoke the appropriate functions depending
  on the return state of the http handler following the graphql implementation's definition:

  success = 200, data returned, no errors -> return only data
  error = 200, data returned, errors exist -> return body, which has { data errors }
  failure = 5xx/0, no data returned, error message -> return response, which has { status status-text etc }
  unauthenticated = 401, no data returned, errors exist -> return body, which has { errors }"
  ([cfg] (dsl->handlers cfg nil))
  ([{:keys [success error failure unauthenticated always]} _]
   (letfn [(handle-response [[ok? result]]
             ((or always (constantly nil)) [ok? result])
             (if ok?
               (handle-success result)
               (handle-error result)))
           (handle-success [body]
                           (if (error? body)
                             (if (unauthenticated? body)
                               (unauthenticated body)
                               (error body))
                             (success (:data body))))
           (handle-error [{:keys [status status-text] :as resp}]
                         (if (failure? status)
                           (when-not (= :aborted (:failure resp))
                             (failure resp))
                           (logging.core/error "We somehow managed to get a server failure that wasn't a 5xx error:" resp)))]
     {:handler handle-response})))

(defn- make-request [cfg]
  (http/ajax-request cfg))

(defn- edn->json
  "Write the given edn into json after converting kebab-case into snake_case"
  [edn]
  (->> edn
       (transform-keys k/->snake_case_keyword)
       (clj->js)
       (.serialize (goog.json.Serializer.))))

(defn- edn->camelCaseJson
  "Because graphql is awesome."
  [edn]
  (->> edn
       (transform-keys k/->camelCaseString)
       (clj->js)
       (.serialize (goog.json.Serializer.))))

(defn- json->edn
  "Write the given json into edn after converting snake_case into kebab-case."
  [jsn]
  (->> jsn
       (json/parse)
       (js->clj)
       (transform-keys k/->kebab-case-keyword)))

(defn- xhr->edn [xhr]
  (-> xhr
      (.getResponseText)
      (json->edn)))

(defmulti request-fmt identity)

(defmethod request-fmt :json [_]
  {:write edn->json
   :content-type "application/json"})

(defmethod request-fmt :graphql [_]
  {:write edn->camelCaseJson
   :content-type "application/json"})

(defmulti response-fmt identity)

(defmethod response-fmt :json [_]
  {:read xhr->edn
   :content-type "application/json"})

(defn request-interceptor [request]
  (if-not (empty? (storage/get "carson.token"))
    (assoc-in request [:headers "Authorization"] (str "Token " (storage/get "carson.token")))
    request))

(def session-interceptor
  (intc/to-interceptor {:name "Session Interceptor"
                        :request request-interceptor}))

(swap! simple/default-interceptors (partial cons session-interceptor))

(defn on-success [evt data]
  (rf/>evt (conj evt data)))

(defn on-error [evt body]
  (rf/>evt (conj evt body)))

(defn on-failure [evt resp]
  (rf/>evt (conj evt resp)))

(defn on-unauthenticated [dsl error body]
  (if (:allow-unauth? dsl)
    (rf/>evt (conj error body))
    (rf/>evt [:unauthenticated])))

(def in-flight (atom {}))

(defn- maybe-cancel-in-flight [dsl]
  (if-not (:cancellable? dsl)
    dsl
    (let [xhrio (get @in-flight dsl)]
      (when (and xhrio (true? (.isActive xhrio)))
        (.abort xhrio))
      dsl)))

(defn- release-in-flight [dsl [ok? result]]
  (swap! in-flight dissoc dsl))

(defn- capture-in-flight [dsl xhrio]
  (if-not (:cancellable? dsl)
    xhrio
    (swap! in-flight assoc dsl xhrio)))

(defn- do-graphql
  "Given a map of {on-condition => event-to-dispatch} (see the DSL), convert
  that data into an invoke-able function chain to pass to our http lib as a
  single handler."
  [{:keys [success
           error
           uri
           failure]
    :or {error [:graphql-default-error]
         failure [:graphql-default-failure]}
    :as dsl}]
  (->> dsl
       (maybe-cancel-in-flight)
       (dsl->handlers {:success (partial on-success success)
                       :error (partial on-error error)
                       :failure (partial on-failure failure)
                       :unauthenticated (partial on-unauthenticated dsl error)
                       :always (when (:cancellable? dsl) (partial release-in-flight dsl))})
       (merge {:method :post
               :uri uri
               :params (:gql dsl)
               :timeout (or (:timeout dsl) (* 30 1000))
               :format (request-fmt :graphql)
               :response-format (response-fmt :json)})
       (make-request)
       (capture-in-flight dsl)))

(defn uri-flags []
  {:brand :graphql-uri-v2
   :partner :graphql-uri-v2})

(defn name->uri [name]
  (get (env) (get (uri-flags) name :graphql-uri)))

(defn- select-uri-then-do-graphql
  "Temporarily override the target uri for a graphql call based on the feature flags enabled for this user."
  [dsl]
  (let [uri (name->uri (:name dsl)) ]
    (do-graphql (assoc dsl :uri uri))))

(defn graphql
  "Provides a data DSL for specifying a single graphql connection and query (or
  mutation).

  :success - everything returned from the graphql server and no errors were
  present on the response body.

  :error - the server returned *something* (a data field will always exist, but
  the data may be partial), but there was also a non-empty errors key on the
  response.

  :failure - there was a server 5xx error, or some other network-based failure.

  :allow-unauth? - almost every invocation of the graphql dsl will want to do
  something when the server returns a 401 (and maybe 403, though not yet
  supported). in those rare cases where you *don't* want an action to be taken
  on 401/403 error (such as a login POST), set this argument to true.

  :cancellable? - when set to true, any graphql requests that enter the http
  pipeline and are exactly identical to an already in-flight request will cause
  the previous request to be cancelled, and the new request to run instead,
  ad-infinitum. identity is checked against the entire dsl map.

  See the ::http.core.specs.graphql for a full description of the available DSL"
  [dsl]
  (s/assert ::sp/spec dsl)
  (cond
    (sequential? dsl) (doseq [individual-dsl dsl] (select-uri-then-do-graphql individual-dsl))
    (map? dsl) (select-uri-then-do-graphql dsl)))
